import { Controller } from "@hotwired/stimulus"

// Renders the end-of-day balance series as a compact area+line sparkline, toggleable between
// daily and weekly (weekly = the average balance within each ISO week). Data is the
// `daily_balances` metric ([{date, balance}, …]); everything is drawn as inline SVG so there's
// no chart dependency. A zero baseline and the minimum point are marked so negative days stand out.
export default class extends Controller {
  static values = { series: Array }
  static targets = ["chart", "readout", "dailyBtn", "weeklyBtn"]

  connect() {
    this.mode = "daily"
    this.render()
  }

  setDaily() { this.mode = "daily"; this.render() }
  setWeekly() { this.mode = "weekly"; this.render() }

  get points() {
    if (this.mode === "daily") return this.seriesValue
    // Group by ISO week (Thursday-anchored), average the balances in each.
    const byWeek = new Map()
    for (const p of this.seriesValue) {
      const key = isoWeekKey(new Date(p.date + "T00:00:00"))
      const g = byWeek.get(key) || { sum: 0, n: 0, date: p.date }
      g.sum += p.balance; g.n += 1
      byWeek.set(key, g)
    }
    return [...byWeek.values()].map((g) => ({ date: g.date, balance: g.sum / g.n }))
  }

  render() {
    const pts = this.points
    this.dailyBtnTarget.classList.toggle("btn-active", this.mode === "daily")
    this.weeklyBtnTarget.classList.toggle("btn-active", this.mode === "weekly")
    if (!pts.length) { this.chartTarget.innerHTML = ""; this.readoutTarget.textContent = "No balance data."; return }

    const W = 640, H = 120, pad = 4
    const vals = pts.map((p) => p.balance)
    const min = Math.min(...vals, 0), max = Math.max(...vals, 0)
    const span = max - min || 1
    const x = (i) => pad + (i * (W - 2 * pad)) / Math.max(1, pts.length - 1)
    const y = (v) => pad + (H - 2 * pad) * (1 - (v - min) / span)
    const zeroY = y(0)

    const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.balance).toFixed(1)}`).join(" ")
    const area = `${line} L${x(pts.length - 1).toFixed(1)},${zeroY.toFixed(1)} L${x(0).toFixed(1)},${zeroY.toFixed(1)} Z`
    const minIdx = vals.indexOf(Math.min(...vals))

    this.chartTarget.innerHTML = `
      <svg viewBox="0 0 ${W} ${H}" class="w-full" preserveAspectRatio="none" role="img" aria-label="Balance trend">
        <path d="${area}" fill="currentColor" opacity="0.08" class="text-primary" />
        <line x1="${pad}" y1="${zeroY.toFixed(1)}" x2="${W - pad}" y2="${zeroY.toFixed(1)}" stroke="currentColor" stroke-width="1" opacity="0.25" stroke-dasharray="3 3" />
        <path d="${line}" fill="none" stroke="currentColor" stroke-width="1.5" class="text-primary" />
        <circle cx="${x(minIdx).toFixed(1)}" cy="${y(vals[minIdx]).toFixed(1)}" r="3" class="${vals[minIdx] < 0 ? "text-error" : "text-primary"}" fill="currentColor" />
      </svg>`

    const latest = pts[pts.length - 1].balance
    this.readoutTarget.textContent =
      `${pts.length} ${this.mode === "daily" ? "days" : "weeks"} · low ${money(Math.min(...vals))} · latest ${money(latest)}`
  }
}

function isoWeekKey(d) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const day = t.getUTCDay() || 7
  t.setUTCDate(t.getUTCDate() + 4 - day)
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1))
  const week = Math.ceil(((t - yearStart) / 86400000 + 1) / 7)
  return `${t.getUTCFullYear()}-W${week}`
}

function money(n) {
  return "$" + Math.round(n).toLocaleString("en-US")
}
